import { AuthGuard } from '@/components/auth'
import { BillingNotificationBanner } from '@/components/billing/BillingNotificationBanner'
import { TeamInitializer } from '@/components/common/TeamInitializer'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { Header } from '@/components/layout/Header'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { WebSocketProvider } from '@/contexts/WebSocketContext'
import { useEffectiveAccess } from '@/hooks/use-access'
import { useTeamStore } from '@/store/team'
import { Users } from 'lucide-react'
import { Outlet } from 'react-router-dom'

/**
 * Guard that prevents page content from rendering when the user has no teams.
 * The sidebar (with "Create Your First Team" button) remains visible.
 */
function TeamRequiredGuard({ children }: { children: React.ReactNode }) {
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  const teams = useTeamStore((s) => s.teams)

  if (!currentTeamId && teams.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <Users className="h-8 w-8 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">No Team Selected</h2>
          <p className="text-sm text-muted-foreground max-w-sm">
            Create a team using the button in the sidebar to get started.
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export function DashboardLayout() {
  // DEV MODE: Allow bypass for development testing
  // Set VITE_SKIP_AUTH=true in .env.local to enable dev bypass
  const allowDevBypass = import.meta.env.VITE_SKIP_AUTH === 'true'

  // Load effective access on mount — populates team store for sidebar filtering + SectionGuard
  useEffectiveAccess()

  return (
    <AuthGuard allowDevBypass={allowDevBypass}>
      <TeamInitializer>
        <WebSocketProvider>
          <SidebarProvider>
            <AppSidebar />
            <SidebarInset>
              <Header />
              <BillingNotificationBanner />
              <main className="flex-1 overflow-x-hidden p-6">
                <TeamRequiredGuard>
                  <Outlet />
                </TeamRequiredGuard>
              </main>
            </SidebarInset>
          </SidebarProvider>
        </WebSocketProvider>
      </TeamInitializer>
    </AuthGuard>
  )
}
